// Email management and sending functions
import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, query, where, orderBy, serverTimestamp, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './config';
import { getGeneralSettings } from './settings';

/**
 * Send email using Firebase Extensions (Trigger Email) or direct email service
 * Supports multiple methods:
 * 1. SMTP via Cloud Function (Recommended for custom mailboxes)
 * 2. Firebase Extension "Trigger Email" - writes to mail collection
 * 3. Direct email service API (SendGrid, Resend, etc.)
 * 4. Cloud Function endpoint
 */
export const sendEmail = async (to, subject, htmlContent, textContent = null, fromEmail = null, fromName = null) => {
  try {
    // Get email service configuration from Firestore settings
    let settings = null;
    try {
      settings = await getGeneralSettings();
    } catch (err) {
      // Ignore permission errors for public users - fallback to defaults
      console.warn('Could not load email settings, using defaults:', err.message);
    }
    const emailMethod = settings?.emailServiceProvider || import.meta.env.VITE_EMAIL_METHOD || 'smtp'; // 'smtp', 'firebase-extension', 'api', 'cloud-function'
    const emailServiceUrl = settings?.emailServiceUrl || import.meta.env.VITE_EMAIL_SERVICE_URL;
    const defaultFromEmail = fromEmail || settings?.emailFromAddress || settings?.smtpUser || import.meta.env.VITE_EMAIL_FROM || 'Fabiha@freespiritfabiha.com';
    const defaultFromName = fromName || settings?.emailFromName || import.meta.env.VITE_EMAIL_FROM_NAME || 'Fabiha @ FreeSpiritFabiha';

    // Store email in Firestore for tracking (always)
    const emailsRef = collection(db, 'emails');
    const emailDoc = {
      to,
      subject,
      htmlContent,
      textContent: textContent || htmlContent.replace(/<[^>]*>/g, ''),
      from: fromEmail || defaultFromEmail,
      fromName: fromName || defaultFromName,
      status: 'pending',
      method: emailMethod,
      createdAt: serverTimestamp()
    };

    // Method 1: SMTP via Cloud Function (Recommended for custom mailboxes)
    if (emailMethod === 'smtp') {
      const sendSMTPEmail = httpsCallable(functions, 'sendSMTPEmail');

      const result = await sendSMTPEmail({
        to,
        subject,
        html: htmlContent,
        text: textContent || htmlContent.replace(/<[^>]*>/g, ''),
        fromEmail: fromEmail || defaultFromEmail,
        fromName: fromName || defaultFromName
      });

      if (result.data?.success) {
        emailDoc.status = 'sent';
        emailDoc.sentAt = serverTimestamp();
        emailDoc.messageId = result.data?.messageId;
      } else {
        throw new Error(result.data?.error || 'SMTP function returned unsuccessful');
      }
    }
    // Method 2: Firebase Extension "Trigger Email"
    // This extension watches the 'mail' collection and sends emails
    else if (emailMethod === 'firebase-extension') {
      const mailRef = collection(db, 'mail');
      await addDoc(mailRef, {
        to: [to], // Array format for Trigger Email extension
        message: {
          subject,
          html: htmlContent,
          text: textContent || htmlContent.replace(/<[^>]*>/g, '')
        },
        // Optional: Add attachments, headers, etc.
      });

      emailDoc.status = 'sent';
      emailDoc.sentAt = serverTimestamp();
    }
    // Method 3: Direct API call to email service
    else if (emailMethod === 'api' && emailServiceUrl) {
      const response = await fetch(emailServiceUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to,
          from: fromEmail || defaultFromEmail,
          fromName: fromName || defaultFromName,
          subject,
          html: htmlContent,
          text: textContent || htmlContent.replace(/<[^>]*>/g, '')
        })
      });

      if (!response.ok) {
        throw new Error(`Email service returned error: ${response.statusText}`);
      }

      emailDoc.status = 'sent';
      emailDoc.sentAt = serverTimestamp();
    }
    // Method 4: Cloud Function endpoint (generic)
    else if (emailMethod === 'cloud-function' && emailServiceUrl) {
      const response = await fetch(emailServiceUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to,
          from: fromEmail || defaultFromEmail,
          fromName: fromName || defaultFromName,
          subject,
          html: htmlContent,
          text: textContent || htmlContent.replace(/<[^>]*>/g, '')
        })
      });

      if (!response.ok) {
        throw new Error(`Cloud Function returned error: ${response.statusText}`);
      }

      emailDoc.status = 'sent';
      emailDoc.sentAt = serverTimestamp();
    }
    // Fallback: Just log to Firestore (for development/testing)
    else {
      emailDoc.status = 'logged';
      emailDoc.note = 'Email logged only - no email service configured';
    }

    // Save email record
    await addDoc(emailsRef, emailDoc);

    return { success: true, method: emailMethod };
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Error sending email:', error);
    }

    // Log failed email
    const emailsRef = collection(db, 'emails');
    await addDoc(emailsRef, {
      to,
      subject,
      htmlContent,
      textContent: textContent || htmlContent.replace(/<[^>]*>/g, ''),
      status: 'failed',
      error: error.message,
      sentAt: serverTimestamp()
    });

    return { success: false, error: error.message };
  }
};

/**
 * Send broadcast email to all active subscribers
 * Uses the sendBroadcastEmail Cloud Function
 * @param {Object} options - Broadcast options
 * @param {string} options.templateId - Template ID to use (optional if subject+htmlContent provided)
 * @param {string} options.subject - Email subject (optional if templateId provided)
 * @param {string} options.htmlContent - Email HTML content (optional if templateId provided) 
 * @param {string} options.customSubject - Override template subject (optional)
 */
export const sendBroadcastEmail = async ({ templateId, subject, htmlContent, customSubject }) => {
  try {
    const broadcastFn = httpsCallable(functions, 'sendBroadcastEmail');
    const result = await broadcastFn({ templateId, subject, htmlContent, customSubject });
    return result.data;
  } catch (error) {
    console.error('Error sending broadcast email:', error);
    return {
      success: false,
      error: error.message || 'Failed to send broadcast email'
    };
  }
};

/**
 * Send email template
 */
export const sendEmailTemplate = async (templateId, to, variables = {}) => {
  try {
    // Get template from Firestore
    const templateRef = doc(db, 'emailTemplates', templateId);
    const templateSnap = await getDoc(templateRef);

    if (!templateSnap.exists()) {
      throw new Error('Email template not found');
    }

    const template = templateSnap.data();
    let subject = template.subject;
    let htmlContent = template.htmlContent;
    let textContent = template.textContent || '';

    // Get subscriber info to generate unsubscribe link
    let unsubscribeUrl = '#';
    try {
      const { getSubscriberByEmail, getUnsubscribeUrl } = await import('./subscribers');
      const subscriber = await getSubscriberByEmail(to);
      if (subscriber) {
        unsubscribeUrl = getUnsubscribeUrl(subscriber.id, subscriber.email);
      }
    } catch (error) {
      console.error('Error generating unsubscribe URL:', error);
    }

    // Replace variables in template (including unsubscribeUrl)
    const allVariables = {
      ...variables,
      unsubscribeUrl: unsubscribeUrl
    };

    Object.keys(allVariables).forEach(key => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      subject = subject.replace(regex, allVariables[key]);
      htmlContent = htmlContent.replace(regex, allVariables[key]);
      textContent = textContent.replace(regex, allVariables[key]);
    });

    return await sendEmail(to, subject, htmlContent, textContent);
  } catch (error) {
    console.error('Error sending email template:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Create email template
 */
export const createEmailTemplate = async (templateData) => {
  try {
    const templatesRef = collection(db, 'emailTemplates');
    const docRef = await addDoc(templatesRef, {
      ...templateData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error creating email template:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all email templates
 */
export const getAllEmailTemplates = async () => {
  try {
    const templatesRef = collection(db, 'emailTemplates');
    const q = query(templatesRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting email templates:', error);
    return [];
  }
};

/**
 * Update email template
 */
export const updateEmailTemplate = async (templateId, updates) => {
  try {
    const templateRef = doc(db, 'emailTemplates', templateId);
    await updateDoc(templateRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating email template:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete email template
 * @param {string} templateId - The template ID to delete
 * @param {boolean} removeFromSequences - If true, removes template references from sequences (orphans them)
 */
export const deleteEmailTemplate = async (templateId, removeFromSequences = false) => {
  try {
    // Check if template is used in any sequences
    const sequencesRef = collection(db, 'emailSequences');
    const sequencesSnapshot = await getDocs(sequencesRef);

    const usedInSequences = [];
    sequencesSnapshot.forEach(doc => {
      const sequence = doc.data();
      if (sequence.emails && Array.isArray(sequence.emails)) {
        const isUsed = sequence.emails.some(email => email.templateId === templateId);
        if (isUsed) {
          usedInSequences.push({
            id: doc.id,
            name: sequence.name || 'Unnamed Sequence'
          });
        }
      }
    });

    // If template is used and we're not removing from sequences, return error
    if (usedInSequences.length > 0 && !removeFromSequences) {
      return {
        success: false,
        error: 'Template is used in email sequences',
        usedIn: usedInSequences
      };
    }

    // If removing from sequences, update all sequences to remove the template reference
    if (usedInSequences.length > 0 && removeFromSequences) {
      const updatePromises = usedInSequences.map(async (seq) => {
        const sequenceRef = doc(db, 'emailSequences', seq.id);
        const sequenceSnap = await getDoc(sequenceRef);
        if (sequenceSnap.exists()) {
          const sequenceData = sequenceSnap.data();
          const updatedEmails = (sequenceData.emails || []).map(email => {
            if (email.templateId === templateId) {
              // Mark as orphaned by removing templateId
              return {
                ...email,
                templateId: null,
                orphaned: true,
                orphanedAt: serverTimestamp()
              };
            }
            return email;
          });

          await updateDoc(sequenceRef, {
            emails: updatedEmails,
            updatedAt: serverTimestamp(),
            hasOrphanedEmails: updatedEmails.some(e => e.orphaned === true)
          });
        }
      });

      await Promise.all(updatePromises);
    }

    // Delete the template
    const templateRef = doc(db, 'emailTemplates', templateId);
    await deleteDoc(templateRef);
    return {
      success: true,
      orphanedSequences: removeFromSequences ? usedInSequences : []
    };
  } catch (error) {
    console.error('Error deleting email template:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Check for orphaned templates in sequences
 * Returns sequences that have emails with missing templates
 */
export const checkOrphanedTemplates = async (sequences, templates) => {
  const templateIds = new Set(templates.map(t => t.id));
  const orphanedSequences = [];

  sequences.forEach(sequence => {
    if (sequence.emails && Array.isArray(sequence.emails)) {
      const orphanedEmails = sequence.emails.filter(email => {
        return email.templateId && !templateIds.has(email.templateId);
      });

      if (orphanedEmails.length > 0) {
        orphanedSequences.push({
          sequenceId: sequence.id,
          sequenceName: sequence.name || 'Unnamed Sequence',
          orphanedCount: orphanedEmails.length,
          orphanedEmails: orphanedEmails
        });
      }
    }
  });

  return orphanedSequences;
};

/**
 * Fix orphaned emails in a sequence by removing them or updating template references
 */
export const fixOrphanedEmails = async (sequenceId, action = 'remove') => {
  try {
    const sequenceRef = doc(db, 'emailSequences', sequenceId);
    const sequenceSnap = await getDoc(sequenceRef);

    if (!sequenceSnap.exists()) {
      return { success: false, error: 'Sequence not found' };
    }

    const sequenceData = sequenceSnap.data();
    const allTemplates = await getAllEmailTemplates();
    const templateIds = new Set(allTemplates.map(t => t.id));

    let updatedEmails;
    if (action === 'remove') {
      // Remove orphaned emails
      updatedEmails = (sequenceData.emails || []).filter(email => {
        return !email.templateId || templateIds.has(email.templateId);
      });
    } else {
      // Keep but mark as orphaned
      updatedEmails = (sequenceData.emails || []).map(email => {
        if (email.templateId && !templateIds.has(email.templateId)) {
          return {
            ...email,
            orphaned: true
          };
        }
        return email;
      });
    }

    await updateDoc(sequenceRef, {
      emails: updatedEmails,
      updatedAt: serverTimestamp(),
      hasOrphanedEmails: updatedEmails.some(e => e.orphaned === true)
    });

    return { success: true };
  } catch (error) {
    console.error('Error fixing orphaned emails:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get email sequence by trigger type
 */
export const getEmailSequence = async (triggerType) => {
  try {
    const sequencesRef = collection(db, 'emailSequences');
    const q = query(
      sequencesRef,
      where('trigger', '==', triggerType),
      where('status', '==', 'active')
    );
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return null;
    }

    // Get the first active sequence (you might want to handle multiple)
    const sequence = snapshot.docs[0].data();
    return {
      id: snapshot.docs[0].id,
      ...sequence
    };
  } catch (error) {
    console.error('Error getting email sequence:', error);
    return null;
  }
};

/**
 * Convert days to hours
 */
export const daysToHours = (days) => {
  return days * 24;
};

/**
 * Convert hours to days (rounded)
 */
export const hoursToDays = (hours) => {
  return Math.floor(hours / 24);
};

/**
 * Create default email sequence based on type
 */
export const createDefaultSequence = async (type) => {
  const defaultSequences = {
    'welcome': {
      name: 'Welcome Series',
      trigger: 'welcome',
      status: 'draft',
      emails: [
        { templateId: '', delay: 0, order: 0 },
        { templateId: '', delay: 48, order: 1 }, // Day 2
        { templateId: '', delay: 120, order: 2 }  // Day 5
      ]
    },
    'purchase': {
      name: 'Purchase Confirmation',
      trigger: 'purchase',
      status: 'draft',
      emails: [
        { templateId: '', delay: 0, order: 0 }
      ]
    },
    'booking-confirmed': {
      name: 'Booking Confirmation',
      trigger: 'booking-confirmed',
      status: 'draft',
      emails: [
        { templateId: '', delay: 0, order: 0 }
      ]
    },
    'course-enrolled': {
      name: 'Course Welcome',
      trigger: 'course-enrolled',
      status: 'draft',
      emails: [
        { templateId: '', delay: 0, order: 0 },
        { templateId: '', delay: 24, order: 1 } // Day 1
      ]
    },
    'membership-activated': {
      name: 'Membership Welcome',
      trigger: 'membership-activated',
      status: 'draft',
      emails: [
        { templateId: '', delay: 0, order: 0 },
        { templateId: '', delay: 24, order: 1 } // Day 1
      ]
    }
  };

  const sequenceData = defaultSequences[type];
  if (!sequenceData) {
    return { success: false, error: `Unknown sequence type: ${type}` };
  }

  return await createEmailSequence(sequenceData);
};

/**
 * Trigger email sequence
 * For immediate emails (delay 0): sends right away
 * For delayed emails: schedules them in Firestore to be processed by a Cloud Function
 */
export const triggerEmailSequence = async (triggerType, recipientData) => {
  try {
    const sequence = await getEmailSequence(triggerType);

    if (!sequence || !sequence.emails || sequence.emails.length === 0) {
      return { success: false, message: `No active email sequence found for trigger: ${triggerType}` };
    }

    const now = new Date();
    const immediateEmails = [];
    const scheduledEmails = [];

    // Process each email in the sequence
    for (const email of sequence.emails) {
      const delay = email.delay || 0; // Hours

      // Calculate schedule time
      // If delay is 0, schedule for NOW (or 1 minute in future to be safe)
      const scheduledFor = new Date(now.getTime() + (delay * 60 * 60 * 1000));

      // Add to scheduled list - backend will pick it up
      scheduledEmails.push({
        ...email,
        scheduledFor
      });
    }

    // Send immediate emails - DEPRECATED: All emails are now scheduled for backend processing
    // to avoid unrestricted client-side email sending permissions.
    // const immediateResults = await Promise.all(...) -> REMOVED

    // Schedule delayed emails in Firestore
    // These will be processed by the processScheduledEmails Cloud Function
    const scheduledEmailsRef = collection(db, 'scheduledEmails');
    const schedulePromises = scheduledEmails.map(email =>
      addDoc(scheduledEmailsRef, {
        sequenceId: sequence.id,
        triggerType,
        templateId: email.templateId,
        recipientEmail: recipientData.email,
        recipientName: recipientData.name || 'Customer',
        variables: recipientData.variables || {},
        scheduledFor: email.scheduledFor,
        delayHours: email.delay,
        status: 'pending', // pending, sent, failed, cancelled
        createdAt: serverTimestamp(),
        attempts: 0,
        maxAttempts: 3
      })
    );

    await Promise.all(schedulePromises);

    // Log sequence trigger
    const triggersRef = collection(db, 'emailSequenceTriggers');
    await addDoc(triggersRef, {
      sequenceId: sequence.id,
      triggerType,
      recipientEmail: recipientData.email,
      recipientName: recipientData.name,
      emailsScheduled: sequence.emails.length,
      immediateEmails: immediateEmails.length,
      scheduledEmails: scheduledEmails.length,
      triggeredAt: serverTimestamp()
    });

    return {
      success: true,
      emailsScheduled: sequence.emails.length,
      immediateEmails: immediateEmails.length,
      delayedEmails: scheduledEmails.length,
      note: scheduledEmails.length > 0
        ? 'Delayed emails saved to Firestore. They will be sent by the Cloud Function scheduler.'
        : undefined
    };
  } catch (error) {
    console.error('Error triggering email sequence:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Create email sequence
 */
export const createEmailSequence = async (sequenceData) => {
  try {
    const sequencesRef = collection(db, 'emailSequences');
    const docRef = await addDoc(sequencesRef, {
      ...sequenceData,
      status: sequenceData.status || 'draft',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error creating email sequence:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all email sequences
 */
export const getAllEmailSequences = async () => {
  try {
    const sequencesRef = collection(db, 'emailSequences');
    const q = query(sequencesRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting email sequences:', error);
    return [];
  }
};

/**
 * Update email sequence
 */
export const updateEmailSequence = async (sequenceId, updates) => {
  try {
    const sequenceRef = doc(db, 'emailSequences', sequenceId);
    await updateDoc(sequenceRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating email sequence:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete email sequence
 */
export const deleteEmailSequence = async (sequenceId) => {
  try {
    const sequenceRef = doc(db, 'emailSequences', sequenceId);
    await deleteDoc(sequenceRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting email sequence:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get email history for a recipient
 */
export const getEmailHistory = async (email) => {
  try {
    const emailsRef = collection(db, 'emails');
    const q = query(
      emailsRef,
      where('to', '==', email),
      orderBy('sentAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting email history:', error);
    return [];
  }
};

/**
 * Get all scheduled emails (pending, sent, failed)
 */
export const getScheduledEmails = async (statusFilter = null) => {
  try {
    const scheduledRef = collection(db, 'scheduledEmails');
    let q;

    if (statusFilter) {
      q = query(
        scheduledRef,
        where('status', '==', statusFilter),
        orderBy('scheduledFor', 'asc')
      );
    } else {
      q = query(scheduledRef, orderBy('scheduledFor', 'desc'));
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      // Convert Firestore Timestamp to Date for easier handling
      scheduledFor: doc.data().scheduledFor?.toDate?.() || doc.data().scheduledFor
    }));
  } catch (error) {
    console.error('Error getting scheduled emails:', error);
    return [];
  }
};

/**
 * Get pending scheduled emails count
 */
export const getPendingEmailsCount = async () => {
  try {
    const scheduledRef = collection(db, 'scheduledEmails');
    const q = query(scheduledRef, where('status', '==', 'pending'));
    const snapshot = await getDocs(q);
    return snapshot.size;
  } catch (error) {
    console.error('Error getting pending emails count:', error);
    return 0;
  }
};

/**
 * Cancel a specific scheduled email
 */
export const cancelScheduledEmail = async (emailId) => {
  try {
    const emailRef = doc(db, 'scheduledEmails', emailId);
    await updateDoc(emailRef, {
      status: 'cancelled',
      cancelledAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error cancelling scheduled email:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Cancel all pending scheduled emails for a recipient
 */
export const cancelAllScheduledEmailsForRecipient = async (recipientEmail) => {
  try {
    const scheduledRef = collection(db, 'scheduledEmails');
    const q = query(
      scheduledRef,
      where('status', '==', 'pending'),
      where('recipientEmail', '==', recipientEmail)
    );
    const snapshot = await getDocs(q);

    const cancelPromises = snapshot.docs.map(emailDoc =>
      updateDoc(doc(db, 'scheduledEmails', emailDoc.id), {
        status: 'cancelled',
        cancelledAt: serverTimestamp()
      })
    );

    await Promise.all(cancelPromises);
    return { success: true, cancelledCount: snapshot.size };
  } catch (error) {
    console.error('Error cancelling scheduled emails:', error);
    return { success: false, error: error.message };
  }
};
